import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Sparkles, X } from 'lucide-react';

const RARITY_STYLES = {
  common: {
    bg: 'from-gray-600/90 to-gray-700/90',
    border: 'border-gray-500',
    glow: '',
    icon: '🏅'
  },
  uncommon: {
    bg: 'from-green-600/90 to-green-700/90',
    border: 'border-green-500',
    glow: 'shadow-green-500/30',
    icon: '🥈'
  },
  rare: {
    bg: 'from-blue-600/90 to-blue-700/90',
    border: 'border-blue-500',
    glow: 'shadow-blue-500/40',
    icon: '🥇'
  },
  epic: {
    bg: 'from-purple-600/90 to-purple-700/90',
    border: 'border-purple-500',
    glow: 'shadow-purple-500/50',
    icon: '💎'
  },
  legendary: {
    bg: 'from-amber-500/90 to-orange-600/90',
    border: 'border-amber-400',
    glow: 'shadow-amber-500/60',
    icon: '🌟'
  },
  mythic: {
    bg: 'from-pink-500/90 via-purple-500/90 to-cyan-500/90',
    border: 'border-pink-400',
    glow: 'shadow-pink-500/70',
    icon: '✨'
  }
};

export const AchievementToast = ({ achievement, onClose }) => {
  if (!achievement) return null;
  
  const style = RARITY_STYLES[achievement.rarity] || RARITY_STYLES.common;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.9 }}
      className={`
        fixed top-4 left-1/2 -translate-x-1/2 z-[200]
        bg-gradient-to-r ${style.bg} backdrop-blur-md
        rounded-xl border-2 ${style.border}
        shadow-2xl ${style.glow}
        p-4 min-w-[280px] max-w-[360px]
      `}
    >
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-white/50 hover:text-white transition-colors"
      >
        <X size={14} />
      </button>
      
      <div className="flex items-center gap-3">
        <div className="text-4xl animate-bounce">{achievement.icon || style.icon}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={14} className="text-yellow-400" />
            <span className="text-[10px] text-yellow-400 font-bold uppercase tracking-wider">
              Achievement Unlocked!
            </span>
          </div>
          <h3 className="font-heading text-white text-lg">{achievement.name}</h3>
          <p className="text-white/70 text-xs">{achievement.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold
              ${achievement.rarity === 'mythic' ? 'bg-gradient-to-r from-pink-500 to-cyan-500 text-white' :
                achievement.rarity === 'legendary' ? 'bg-amber-500 text-black' :
                achievement.rarity === 'epic' ? 'bg-purple-500 text-white' :
                achievement.rarity === 'rare' ? 'bg-blue-500 text-white' :
                achievement.rarity === 'uncommon' ? 'bg-green-500 text-white' :
                'bg-gray-500 text-white'
              }`}>
              {achievement.rarity}
            </span>
            <span className="text-[10px] text-white/50">{achievement.category}</span>
          </div>
        </div>
      </div>
      
      {/* Particle effects for rare+ achievements */}
      {['rare', 'epic', 'legendary', 'mythic'].includes(achievement.rarity) && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
          {[
            { x: 15, y: 20, d: 1.2, delay: 0.1, rd: 0.5 },
            { x: 85, y: 30, d: 1.5, delay: 0.3, rd: 1.2 },
            { x: 25, y: 70, d: 1.1, delay: 0.2, rd: 0.8 },
            { x: 75, y: 80, d: 1.8, delay: 0.4, rd: 1.5 },
            { x: 45, y: 15, d: 1.3, delay: 0.15, rd: 0.3 },
            { x: 55, y: 85, d: 1.6, delay: 0.35, rd: 1.0 },
            { x: 10, y: 50, d: 1.4, delay: 0.25, rd: 0.7 },
            { x: 90, y: 55, d: 1.7, delay: 0.45, rd: 1.3 },
            { x: 35, y: 40, d: 1.0, delay: 0.05, rd: 0.4 },
            { x: 65, y: 60, d: 1.9, delay: 0.5, rd: 1.8 },
            { x: 20, y: 90, d: 1.25, delay: 0.12, rd: 0.6 },
            { x: 80, y: 10, d: 1.55, delay: 0.38, rd: 1.1 },
          ].map((p, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-white/60"
              initial={{ 
                x: '50%', 
                y: '50%', 
                opacity: 1 
              }}
              animate={{ 
                x: `${p.x}%`, 
                y: `${p.y}%`,
                opacity: 0
              }}
              transition={{ 
                duration: p.d, 
                delay: p.delay,
                repeat: Infinity,
                repeatDelay: p.rd
              }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Hook to manage achievement notifications
import { useState, useCallback } from 'react';

export const useAchievementNotification = () => {
  const [achievement, setAchievement] = useState(null);
  
  const showAchievement = useCallback((ach) => {
    setAchievement(ach);
    // Auto-close after 5 seconds
    setTimeout(() => setAchievement(null), 5000);
  }, []);
  
  const hideAchievement = useCallback(() => {
    setAchievement(null);
  }, []);
  
  return { achievement, showAchievement, hideAchievement };
};

export default AchievementToast;
